import {
  METRIC_AVERAGING_DENOMINATORS,
  METRIC_LABEL_PREFIX,
} from '@/app/dashboards/reportConstants';
import { DEFAULT_METRICS_BY_REPORT_CATEGORY } from '@/app/dashboards/utils/reports.enum';
import {
  CONNECTION_TYPE_SECTIONS,
  CONTENT_METRIC_SECTION_TITLES,
  CONTENT_METRIC_SECTION_TOOLTIPS,
  CONTENT_METRIC_SECTIONS,
  POST_TYPE_SECTIONS,
  POST_TYPES,
  QUERY_TIMEFRAME,
} from '@/models/dashboards/metrics.constants';

/**
 * Generates the formula string for an averaged metric.
 * Used by both report tooltips and metric selector tooltips.
 *
 * @param {Object} params
 * @param {?string} [params.baseMetricName] - The display name of the base metric (e.g., "Likes")
 * @param {?string} [params.timeframe] - The metric's timeframe (e.g., QUERY_TIMEFRAME.ACTIVITY_DURING_TIMEFRAME)
 * @param {?string} [params.postType] - The metric's post type (e.g., POST_TYPES.PROMOTED)
 * @returns {string|null} The formula string (e.g., "Avg. Likes = Likes / Posts Published In Timeframe") or null
 */
export function getAveragedMetricFormula({ baseMetricName, timeframe, postType }) {
  if (!baseMetricName) return null;

  let denominator;
  if (timeframe === QUERY_TIMEFRAME.ACTIVITY_DURING_TIMEFRAME && postType === POST_TYPES.PROMOTED) {
    denominator = METRIC_AVERAGING_DENOMINATORS.PROMOTED_POSTS_PUBLISHED_IN_TIMEFRAME;
  } else if (timeframe === QUERY_TIMEFRAME.ACTIVITY_DURING_TIMEFRAME) {
    denominator = METRIC_AVERAGING_DENOMINATORS.ACTIVITY_DURING_TIMEFRAME;
  } else {
    denominator = METRIC_AVERAGING_DENOMINATORS.POSTS_PUBLISHED_IN_TIMEFRAME;
  }

  return `${METRIC_LABEL_PREFIX.AVERAGE}${baseMetricName} = ${baseMetricName} / ${denominator}`;
}

function getOptionPostTypeSection(option) {
  if (option.channels?.length > 1) return POST_TYPE_SECTIONS.OTHER;
  if (!option.deprecated) return option.postType || POST_TYPE_SECTIONS.OTHER;
  if (option.postType === POST_TYPES.ORGANIC) return POST_TYPE_SECTIONS.DEPRECATED_ORGANIC;
  return POST_TYPE_SECTIONS.DEPRECATED_ORGANIC_PROMOTED;
}

export function getOptionConnectionStatusSection(option) {
  if (option.connectedAccountsOnly) return CONNECTION_TYPE_SECTIONS.CONNECTED_ONLY;
  return CONNECTION_TYPE_SECTIONS.PUBLIC_DATA;
}

export function getOptionsByConnectionStatusSection(options = []) {
  return (options ?? []).reduce((acc, metric) => {
    const type = getOptionConnectionStatusSection(metric);
    if (!acc?.[type]) acc[type] = [];
    acc[type].push(metric);
    return acc;
  }, {});
}

export function getOptionsBySection(options = []) {
  const addToAcc = (acc, type, metric) => {
    if (!acc?.[type]) acc[type] = [];
    acc[type].push(metric);
  };
  return options.reduce((acc, metric) => {
    const categories = metric.metricCategories;
    if (categories?.length > 0) {
      categories.forEach((category) => addToAcc(acc, category, metric));
    } else {
      const postType = getOptionPostTypeSection(metric);
      addToAcc(acc, postType, metric);
    }
    return acc;
  }, {});
}

export function formatMetricOptionsList(options, byConnectionStatus = false) {
  const optionsByGroup = byConnectionStatus
    ? getOptionsByConnectionStatusSection(options)
    : getOptionsBySection(options);

  return Object.values(CONTENT_METRIC_SECTIONS).reduce((acc, groupName) => {
    if (!optionsByGroup?.[groupName]) return acc;

    if (CONTENT_METRIC_SECTION_TITLES?.[groupName])
      acc.push({
        title: CONTENT_METRIC_SECTION_TITLES[groupName],
        tooltip: CONTENT_METRIC_SECTION_TOOLTIPS[groupName],
      });
    acc.push(...optionsByGroup[groupName]);
    return acc;
  }, []);
}

export function getDefaultMetricByReportCategory(category, reportType, availableMetrics) {
  if (!category || !reportType || !availableMetrics) {
    return null;
  }

  const categoryDefaults = DEFAULT_METRICS_BY_REPORT_CATEGORY[category];
  if (!categoryDefaults) {
    return null;
  }

  const defaultMetricKey = categoryDefaults[reportType];

  if (!defaultMetricKey) {
    return null;
  }

  let metricsArray = availableMetrics;
  if (typeof availableMetrics === 'object' && !Array.isArray(availableMetrics)) {
    metricsArray = Object.values(availableMetrics).flat();
  }

  if (!Array.isArray(metricsArray) || metricsArray.length === 0) {
    return null;
  }

  return metricsArray.find((option) => option.value === defaultMetricKey);
}
